import React, { useState } from 'react';
import { SmallText } from 'ds/components/typography';
import ChevronDown from 'assets/svg/chevron-down.svg';
import TableSortIcon from 'assets/svg/table-sort.svg';
import { InsightType } from 'utils/entities';
import { reject, isNil } from 'lodash';
import { BreakpointValue, ShowAt } from 'consts/breakpoints';
import {
  TableRow,
  TableColumn,
  TableHeader,
  TableHeadColumn,
  TableSortIconWrapper,
  TableBody,
  TableWrapper,
  XScrollableTableWrapper,
  XScrollableTable,
  LoaderWrapper,
  RowsWrapper,
  ExpandRowWrapper,
} from './styled';
import { Pagination, PaginationType } from 'ds/components/pagination';
import { Loader } from 'components/loader';
import { useInterval } from 'hooks/useInterval';

export type SortDirection = 'asc' | 'desc';

export interface TableColumn<T> {
  label: string;
  labelNode?: React.ReactNode;
  sortFn?: (items: T[]) => T[];
  renderFn: (item: T, isExpanded?: boolean) => JSX.Element;
  width: string;
  minWidth?: string;
  align?: 'left' | 'right';
  gap?: number;
  customClassName?: string;
}

interface XScrollCfg {
  toRange: BreakpointValue;
  fixedColumns: number;
  fixedTableWidth: string;
  scrollTableWidth: string;
  xScrollableExtendedWidth?: number;
}

type TableFeature = 'pagination' | 'sort';

export interface TableProps<T> {
  data: T[];
  wrapColumnText?: boolean;
  columns: Array<TableColumn<T>>;
  fixedRowFilter?: (row: T, id: number) => boolean;
  visibleRows: number;
  initialSortColumn?: number;
  insightType?: InsightType;
  isExpandable?: boolean;
  rowExpandableGetter?: (dataItem: T) => boolean;
  paginationType?: PaginationType;
  xScroll?: XScrollCfg;
  sidePadding?: number;
  onRowMouseEnter?: (row: T, id: number) => void;
  onRowMouseLeave?: (row: T, id: number) => void;
  onRowClick?: (row: T, id: number) => void;
  onRowExpand?: (dataItem: T) => void;
  rowCustomClassName?: string;
  alwaysShowExpandIcon?: boolean;
  activeRowFilter?: (row: T, id: number) => boolean;
  showExpandOnRowFn?: (row: T) => boolean;
  expandCol?: React.FC<{ item: T }>;
  gatedFeatures?: TableFeature[];
  onGatedFeatureClick?: (featureType: TableFeature, bypassCb: () => void) => void;
}
export const Table = <T extends any>(props: TableProps<T> ) => {
  const {
    columns,
    data: originalData,
    visibleRows,
    sidePadding,
    paginationType,
    fixedRowFilter,
    initialSortColumn = null,
    insightType,
    isExpandable: expandable,
    rowExpandableGetter,
    wrapColumnText,
    xScroll,
    onRowMouseEnter,
    onRowMouseLeave,
    onRowClick,
    onRowExpand,
    rowCustomClassName,
    activeRowFilter,
    expandCol,
    showExpandOnRowFn,
    gatedFeatures = [],
    onGatedFeatureClick,
  } = props;
  const [ sortColumn, setSortColumn ] = useState(initialSortColumn);
  const [ sortDirection, setSortDirection ] = useState<SortDirection>('desc');
  const [ activePage, setActivePage ] = useState<number>(1);
  const [ expandedRow, setExpandedRow ] = useState<number>(null);
  const [ showLoader, setShowLoader ] = useState<boolean>(false);

  const data = [ ...originalData ];

  const maybeReverse = (items: T[]) => sortDirection === 'desc' ? items : [ ...items ].reverse();

  const fixedRows = fixedRowFilter ? data.filter(fixedRowFilter) : [];
  const sortableRows = fixedRowFilter ? reject(data, fixedRowFilter) : data;

  const sortedData = sortColumn !== null && columns[sortColumn] && columns[sortColumn].sortFn ? maybeReverse(columns[sortColumn].sortFn(sortableRows)) : sortableRows;
  useInterval(() =>  {
    if (showLoader) {
      setActivePage(1);
      setShowLoader(false);
    }
  }, 1000, [ showLoader ]);

  const makeSortColumnHandler = (id: number) => () => {
    if (activePage !== 1) {
      setShowLoader(true);
    }
    if (sortColumn === id) {
      if (gatedFeatures.includes('sort') && onGatedFeatureClick) {
        onGatedFeatureClick('sort', () => {
          setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        });
      }
      else setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    }
    else if (columns[id].sortFn) {
      if (gatedFeatures.includes('sort') && onGatedFeatureClick) {
        onGatedFeatureClick('sort', () => {
          setSortColumn(id);
        });
      }
      else setSortColumn(id);
    }
  };

  // expandable doesn't work if xScroll is enabled
  const isExpandable = expandable && !xScroll;

  const renderItems = (items: T[], cols: Array<TableColumn<T>>) => items.map((dataItem, id: number) => {
    const rowId = originalData.indexOf(dataItem);
    const isExpanded = expandedRow === rowId;
    const isRowExpandable = isExpandable && (showExpandOnRowFn ? showExpandOnRowFn(dataItem) : true) && (rowExpandableGetter ? rowExpandableGetter(dataItem) : true);
    const isActive = activeRowFilter ? activeRowFilter(dataItem, id) : false;
    const ExpandRow = expandCol || null;

    return (
      <TableRow
        data-auto={`table-row-${rowId}`}
        className={`table-row ${rowCustomClassName || ''} ${isActive ? 'table-row-active' : ''}`}
        onMouseEnter={onRowMouseEnter ? () => onRowMouseEnter(dataItem, id) : undefined}
        onMouseLeave={onRowMouseLeave ? () => onRowMouseLeave(dataItem, id) : undefined}
        isExpanded={isExpanded}
        key={rowId}
        wrapColumnText={wrapColumnText}
        isExpandable={isExpandable}
        isFixed={fixedRowFilter ? fixedRowFilter(dataItem, id) : false}
        alwaysShowExpandIcon
        onClick={onRowClick ? () => onRowClick(dataItem, id) : (() => isExpandable && isRowExpandable ? toggleExpandedRow(rowId, dataItem) : undefined)}
        isActive={isActive}
        wrapItems={true}
      >
        <RowsWrapper>
          {cols.map(column => (
            <TableColumn
              key={column.label}
              align={column.align}
              width={column.width}
              minWidth={column.minWidth}
              gap={column.gap}
              className={`table-cell ${column.customClassName || ''}`}
            >
              {column.renderFn(dataItem, isExpandable ? isExpanded : false)}
            </TableColumn>
          ))}
          {isRowExpandable ? (
            <div style={{ width: 0 }} className="expand-toggler">
              <ChevronDown width={24} height={24} />
            </div>
          ) : <div style={{ width: 0 }} />}
        </RowsWrapper>
        {isExpanded && ExpandRow && isRowExpandable ? (
          <ExpandRowWrapper>
            <ExpandRow item={dataItem} />
          </ExpandRowWrapper>
        ) : null}
      </TableRow>
    );
  });

  const renderHeader = (cols: Array<TableColumn<T>>) => (
    <TableHeader isExpandable={isExpandable}>
      <TableRow wrapColumnText={wrapColumnText} className="table-row">
        {cols.map((column) => (
          <TableColumn
            key={column.label}
            onClick={makeSortColumnHandler(columns.indexOf(column))}
            align={column.align}
            width={column.width}
            minWidth={column.minWidth}
            gap={column.gap}
            className={`table-cell ${column.customClassName || ''}`}
          >
            <TableHeadColumn align={column.align}>
              {column.labelNode ? column.labelNode : (
                <SmallText weight="bold">{column.label}</SmallText>
              )}
              {sortColumn === columns.indexOf(column) ? (
                <TableSortIconWrapper direction={sortDirection} type={insightType}>
                  <TableSortIcon />
                </TableSortIconWrapper>
              ) : null}
            </TableHeadColumn>
          </TableColumn>
        ))}
      </TableRow>
    </TableHeader>
  );

  const renderBody = (cols: Array<TableColumn<T>>) => (
    <TableBody isExpandable={isExpandable} className="tbody">
      {renderItems(fixedRows, cols)}
      {renderItems(paginationType === 'infinite'
        ? sortedData.slice(0, visibleRows * activePage)
        : sortedData.slice((activePage - 1) * visibleRows, (activePage - 1) * visibleRows + visibleRows)
      , cols)}
    </TableBody>
  );

  const renderTable = (cols: Array<TableColumn<T>>) => showLoader ? (
    <LoaderWrapper>
      <Loader transparentBackground />
    </LoaderWrapper>
  ) : (
    <TableWrapper sidePadding={sidePadding} className="table">
      {renderHeader(cols)}
      {renderBody(cols)}
    </TableWrapper>
  );

  const toggleExpandedRow = (row: number, dataItem: T) => {
    const newExpandedRow = expandedRow === row ? null : row;
    setExpandedRow(newExpandedRow);
    if (onRowExpand && !isNil(newExpandedRow)) onRowExpand(dataItem);
  };

  const totalPages = Math.ceil(sortedData.length / visibleRows);

  return (
    <>
      {xScroll ? (
        <>
          <ShowAt to={xScroll.toRange}>
            <XScrollableTableWrapper fixedTableWidth={xScroll.fixedTableWidth} xScrollableExtendedWidth={xScroll.xScrollableExtendedWidth}>
              {renderTable(columns.slice(0, xScroll.fixedColumns))}
              <XScrollableTable scrollTableWidth={xScroll.scrollTableWidth}>
                {renderTable(columns.slice(xScroll.fixedColumns))}
              </XScrollableTable>
            </XScrollableTableWrapper>
          </ShowAt>
          {xScroll.toRange < 5 ? (
            <ShowAt from={xScroll.toRange + 1 as BreakpointValue}>
              {renderTable(columns)}
            </ShowAt>
          ) : null}
        </>
      ) : renderTable(columns)}
      {paginationType && totalPages > 1 ? (
        <Pagination
          totalPages={totalPages}
          onSetActivePage={gatedFeatures.includes('pagination') && onGatedFeatureClick
            ? (page: number) => onGatedFeatureClick('pagination', () => setActivePage(page))
            : setActivePage}
          activePage={activePage}
          paginationType={paginationType}
        />
      ) : null}
    </>
  );
};
